import React, { useState } from 'react';
import { useRootBackground } from '@/theme/pageGround';

import type { AppType } from '@/types';
import { COLORS } from '@/theme/colors';
import { getProduct } from '@/product';

interface LoadingScreenProps {
    message?: string;
    appType?: AppType;
}

export const LoadingScreen: React.FC<LoadingScreenProps> = ({
    message = 'Loading...',
    appType = 'greekbrain'
}) => {
    const product = getProduct(appType);
    const isHallouminati = appType === 'hallouminati';
    const mascotImage = product.mascot;
    const bgColor = product.loadingBackground;
    // Both apps use the brand green here; kept as one expression so a
    // future GreekBrain accent only needs this line.
    const primaryColor = COLORS.primary;


    // One random pick per mount for the generic messages.
    const [randomSeed] = useState(() => Math.random());
    const pool = product.loadingMessages;
    const displayMessage = pool.length > 0 && (message === 'Loading...' || message === 'Authenticating...')
        ? pool[Math.floor(randomSeed * pool.length)]
        : message;

    useRootBackground(bgColor);


    return (
        <div className="flex flex-col items-center justify-center min-h-screen relative overflow-hidden" style={{ backgroundColor: bgColor }}>

            {/* Mascot Container */}
            <div className={`relative mb-10 flex items-center justify-center ${isHallouminati ? '' : 'animate-bounce-slow'}`}>
                {isHallouminati ? (
                    <>
                        {/* Sharp SVG Rays */}
                        <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-0">
                            <svg
                                viewBox="0 0 100 100"
                                className="w-[500px] h-[500px] opacity-20 animate-spin-slow"
                                style={{ color: 'var(--color-gold-500)' }}
                            >
                                {/* 12 Rays */}
                                <path fill="currentColor" d="M50 0L53 35L50 40L47 35L50 0Z" />
                                <path fill="currentColor" d="M50 100L53 65L50 60L47 65L50 100Z" />
                                <path fill="currentColor" d="M0 50L35 47L40 50L35 53L0 50Z" />
                                <path fill="currentColor" d="M100 50L65 47L60 50L65 53L100 50Z" />

                                <path fill="currentColor" d="M14.6 14.6L39.4 39.4L35.8 42.9L14.6 14.6Z" transform="rotate(45 50 50)" />
                                <path fill="currentColor" d="M85.4 85.4L60.6 60.6L64.2 57.1L85.4 85.4Z" transform="rotate(45 50 50)" />
                                <path fill="currentColor" d="M85.4 14.6L60.6 39.4L57.1 35.8L85.4 14.6Z" transform="rotate(-45 50 50)" />
                                <path fill="currentColor" d="M14.6 85.4L39.4 60.6L42.9 64.2L14.6 85.4Z" transform="rotate(-45 50 50)" />

                                <path fill="currentColor" d="M25 6.7L42.7 37.3L40 40L37.3 42.7L25 6.7Z" transform="rotate(22.5 50 50)" />
                                <path fill="currentColor" d="M75 93.3L57.3 62.7L60 60L62.7 57.3L75 93.3Z" transform="rotate(22.5 50 50)" />

                                <path fill="currentColor" d="M93.3 25L62.7 42.7L60 40L57.3 37.3L93.3 25Z" transform="rotate(22.5 50 50)" />
                                <path fill="currentColor" d="M6.7 75L37.3 57.3L40 60L42.7 62.7L6.7 75Z" transform="rotate(22.5 50 50)" />

                                <path fill="currentColor" d="M6.7 25L37.3 42.7L40 40L42.7 37.3L6.7 25Z" transform="rotate(-22.5 50 50)" />
                                <path fill="currentColor" d="M93.3 75L62.7 57.3L60 60L57.3 62.7L93.3 75Z" transform="rotate(-22.5 50 50)" />

                                <path fill="currentColor" d="M75 6.7L57.3 37.3L60 40L62.7 42.7L75 6.7Z" transform="rotate(-22.5 50 50)" />
                                <path fill="currentColor" d="M25 93.3L42.7 62.7L40 60L37.3 57.3L25 93.3Z" transform="rotate(-22.5 50 50)" />
                            </svg>
                        </div>

                        <img
                            src={mascotImage}
                            alt="Loading"
                            className="relative w-40 h-40 object-contain z-10 drop-shadow-xl"
                        />
                    </>
                ) : (
                    <>
                        <div
                            className="absolute inset-0 rounded-full blur-3xl animate-pulse"
                            style={{ backgroundColor: `${primaryColor}33` }}
                        ></div>
                        <img
                            src={mascotImage}
                            alt="Loading"
                            className="relative w-32 h-32 object-contain animate-float"
                        />
                    </>
                )}
            </div>

            {/* Spinner and Text Container */}
            <div className="flex flex-col items-center z-10 gap-4">
                {/* Minimalist Spinner */}
                {isHallouminati ? (
                    <div className="relative">
                        <div className="animate-spin rounded-full h-8 w-8 border-[3px]"
                            style={{
                                borderColor: 'rgba(255, 210, 0, 0.2)',
                                borderTopColor: 'var(--color-gold-500)'
                            }}
                        ></div>
                    </div>
                ) : (
                    <div className="relative">
                        <div
                            className="animate-spin rounded-full h-8 w-8 border-4"
                            style={{ borderColor: `${primaryColor}4D` }}
                        ></div>
                        <div
                            className="absolute top-0 left-0 animate-spin rounded-full h-8 w-8 border-4 border-t-transparent"
                            style={{ borderColor: primaryColor }}
                        ></div>
                    </div>
                )}

                {/* Loading Text */}
                <h3 className={`font-medium tracking-wide text-center px-4 ${isHallouminati ? 'font-yanone text-base text-warning-text ' : 'text-sm text-ink-faint animate-pulse'}`}

                >
                    {displayMessage}
                </h3>
            </div>
        </div>
    );
};